import { ContactEntity } from '../entities/contact/contact.options';
import { ContactType } from '../entities/contact/contact.type';

export enum DefaultUsersIds {
    JmsAdmin = '00000000-0000-0000-0000-000000000001',
    WorkBuddyImporter = '00000000-0000-0000-0000-000000000002',
    AnonymousUser = '00000000-0000-0000-0000-000000000003',
    ExternalUser = '00000000-0000-0000-0000-000000000006',
    WorkBuddyConnect = '00000000-0000-0000-0000-000000000004',
    WorkBuddyRecurringJobScheduler = '00000000-0000-0000-0000-000000000005',
    WorkBuddyJobAutoCloseScheduler = '00000000-0000-0000-0000-000000000008',
    WorkBuddyReminderScheduler = '00000000-0000-0000-0000-000000000009',
    PhoneSystem = '00000000-0000-0000-0000-000000000006',
    ApiKeyUser = '00000000-0000-0000-0000-000000000007',
    SCIMUser = '00000000-0000-0000-0000-000000000008',
    WorkBuddy = '00000000-0000-0000-0000-000000009999',
    HomesVICIntegration = '00000000-0000-0000-0000-000000000010',
    ManhattanOneIntegration = '00000000-0000-0000-0000-000000000011',

    WorkBuddyAgent = '90000000-0000-0000-0000-000000009999',

    Me = '10000000-0000-0000-0000-000000000001',
}

export enum SystemTeamIds {
    AllCustomers = 'ffffffff-0000-0000-0000-000000000002',
    AllContractors = 'ffffffff-0000-0000-0000-000000000003',
    AllEmployees = 'ffffffff-0000-0000-0000-000000000001',
}

export const SystemTeams: Record<SystemTeamIds, ContactEntity> = {
    [SystemTeamIds.AllCustomers]: { _id: SystemTeamIds.AllCustomers, name: 'All Customers', type: ContactType.Team },
    [SystemTeamIds.AllContractors]: { _id: SystemTeamIds.AllContractors, name: 'All Contractors', type: ContactType.Team },
    [SystemTeamIds.AllEmployees]: { _id: SystemTeamIds.AllEmployees, name: 'All Employees', type: ContactType.Team },
};

const SystemTeamContactTypes: Record<SystemTeamIds, ContactType[]> = {
    [SystemTeamIds.AllCustomers]: [ContactType.ServiceProvider, ContactType.Business, ContactType.Individual],
    [SystemTeamIds.AllContractors]: [ContactType.Subcontractor],
    [SystemTeamIds.AllEmployees]: [ContactType.Employee],
};

export function isSystemTeamId(id: string | null | undefined): id is SystemTeamIds {

    return !!id && Object.values(SystemTeamIds).includes(id as SystemTeamIds);
}

export function getSystemTeam(id: string | null | undefined): ContactEntity | null {

    return isSystemTeamId(id) ? SystemTeams[id] : null;
}

export function getSystemTeamIdsForContactType(type: ContactType | null | undefined): SystemTeamIds[] {

    if (!type) return [];

    return Object.values(SystemTeamIds).filter(teamId => SystemTeamContactTypes[teamId].includes(type));
}

export function isContactSystemTeamMember(contact: { type?: ContactType } | null | undefined, teamId: string | null | undefined): boolean {

    return !!contact?.type && isSystemTeamId(teamId) && SystemTeamContactTypes[teamId].includes(contact.type);
}

export function isContactTeamMember(contact: { type?: ContactType; parents?: ContactEntity[] } | null | undefined, teamId: string | null | undefined): boolean {

    if (!contact || !teamId) return false;

    return (contact.parents ?? []).some(parent => parent.type === ContactType.Team && parent._id === teamId) || isContactSystemTeamMember(contact, teamId);
}

export function getContactTeamIds(contact: { type?: ContactType; parents?: ContactEntity[] } | null | undefined): string[] {

    if (!contact) return [];

    const parentTeamIds = (contact.parents ?? []).filter(parent => parent.type === ContactType.Team).map(parent => parent._id);
    const systemTeamIds = getSystemTeamIdsForContactType(contact.type);

    return [...parentTeamIds, ...systemTeamIds];
}

export function getContactTypeMembershipForSystemTeams(teamIds: string[]): ContactType[] {

    const contactTypes = teamIds.filter(isSystemTeamId).flatMap(teamId => SystemTeamContactTypes[teamId]);
    return Array.from(new Set(contactTypes));
}

export class DefaultUsers {
    public static readonly JmsAdmin: ContactEntity = { _id: DefaultUsersIds.JmsAdmin, name: 'JMS Admin', type: ContactType.SystemAdministrator };
    public static readonly WorkBuddy: ContactEntity = { _id: DefaultUsersIds.WorkBuddy, name: 'WorkBuddy', type: ContactType.System };
    public static readonly WorkBuddyImporter: ContactEntity = { _id: DefaultUsersIds.WorkBuddyImporter, name: 'WorkBuddy Importer', type: ContactType.System };
    public static readonly WorkBuddyConnect: ContactEntity = { _id: DefaultUsersIds.WorkBuddyConnect, name: 'WorkBuddy Connect', type: ContactType.System };
    public static readonly WorkBuddyRecurringJobScheduler: ContactEntity = { _id: DefaultUsersIds.WorkBuddyRecurringJobScheduler, name: 'WorkBuddy Recurring Job Scheduler', type: ContactType.System };
    public static readonly WorkBuddyJobAutoCloseScheduler: ContactEntity = { _id: DefaultUsersIds.WorkBuddyJobAutoCloseScheduler, name: 'WorkBuddy Job Auto Close Scheduler', type: ContactType.System };
    public static readonly WorkBuddyReminderScheduler: ContactEntity = { _id: DefaultUsersIds.WorkBuddyReminderScheduler, name: 'WorkBuddy Reminder Scheduler', type: ContactType.System };
    public static readonly AnonymousUser: ContactEntity = { _id: DefaultUsersIds.AnonymousUser, name: 'Anonymous User', type: ContactType.System };
    public static readonly ExternalUser: ContactEntity = { _id: DefaultUsersIds.AnonymousUser, name: 'External User', type: ContactType.System };
    public static readonly MeUser: ContactEntity = { _id: DefaultUsersIds.Me, name: 'Me', type: ContactType.System };
    public static readonly SCIMUser: ContactEntity = { _id: DefaultUsersIds.SCIMUser, name: 'SCIM User', type: ContactType.System };
    public static readonly ApiKeyUser: ContactEntity = { _id: DefaultUsersIds.ApiKeyUser, name: 'API Key', type: ContactType.System };

    public static readonly HomesVICIntegration: ContactEntity = { _id: DefaultUsersIds.HomesVICIntegration, name: 'HomesVIC Integration', type: ContactType.System };
    public static readonly ManhattanOneIntegration: ContactEntity = { _id: DefaultUsersIds.ManhattanOneIntegration, name: 'Manhattan ONE Integration', type: ContactType.System };
    public static readonly WorkbuddyAgent: ContactEntity = { _id: DefaultUsersIds.WorkBuddyAgent, name: 'Workbuddy Agent', type: ContactType.System };


}
